import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import RevealOnScroll from "./RevealOnScroll";
import SectionEyebrow from "./SectionEyebrow";
import Button from "./Button";
import NewsArticleCard from "./NewsArticleCard";
import NewsFigure from "./NewsFigure";
import { formatNewsDate, PILLAR_DOT } from "../data/newsArticles";
import { articleSchema, breadcrumbSchema } from "../lib/schemas";

const Block = ({ block }) => {
  switch (block.type) {
    case "paragraph":
      return (
        <p
          className="text-[17px] leading-[1.7] mb-6"
          style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
        >
          {block.text}
        </p>
      );
    case "h3":
      return (
        <h3 className="font-display text-[26px] md:text-[32px] leading-[1.15] text-dodgers mt-14 mb-4">
          {block.text}
        </h3>
      );
    case "pullquote":
      return (
        <blockquote
          className="my-10 border-l-4 border-lime pl-6 md:pl-8 py-5 md:py-6 pr-5"
          style={{ background: "rgba(192,255,87,0.05)" }}
        >
          <p
            className="text-[22px] md:text-[26px] leading-[1.4] text-dodgers"
            style={{ fontFamily: "'Hanken Grotesk', sans-serif", fontStyle: "italic" }}
          >
            “{block.text}”
          </p>
          {block.attribution && (
            <cite
              className="block mt-4 not-italic font-condensed uppercase tracking-widest text-[12px] text-mandrogni"
            >
              — {block.attribution}
            </cite>
          )}
        </blockquote>
      );
    case "figure":
      return <NewsFigure src={block.src} caption={block.caption} alt={block.alt} />;
    case "list": {
      const Tag = block.ordered ? "ol" : "ul";
      return (
        <Tag
          className={`${block.ordered ? "list-decimal" : "list-disc"} pl-6 mb-6 space-y-2`}
          style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
        >
          {block.items.map((it, i) => (
            <li key={i} className="text-[17px] leading-[1.6]">
              {it}
            </li>
          ))}
        </Tag>
      );
    }
    case "highlight":
      return (
        <div
          className="my-8 border border-lime p-5 md:p-6"
          style={{ background: "rgba(192,255,87,0.1)" }}
        >
          <p
            className="text-[17px] leading-[1.6] text-dodgers font-bold"
            style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
          >
            {block.text}
          </p>
        </div>
      );
    case "separator":
      return (
        <div
          className="my-10 text-center text-mandrogni"
          style={{ fontFamily: "'Hanken Grotesk', sans-serif", letterSpacing: "0.6em" }}
          aria-hidden="true"
        >
          •&nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp;•
        </div>
      );
    default:
      return null;
  }
};

const NewsArticleLayout = ({ article, related = [] }) => {
  const dot = PILLAR_DOT[article.pillarPrimary] || "bg-lime";

  const articleJsonLd = articleSchema(article);
  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "News", url: "/news" },
    { name: article.title, url: `/news/${article.slug}` },
  ]);

  return (
    <>
      {/* JSON-LD: Article + BreadcrumbList */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      {/* HERO */}
      <section
        className="bg-dodgers text-galactico pt-32 pb-16 md:pt-40 md:pb-20"
        data-testid="article-hero"
      >
        <div className="container-os">
          <RevealOnScroll>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 text-galactico/70 hover:text-lime transition-colors font-condensed uppercase tracking-widest text-sm"
              data-testid="back-to-news"
            >
              <ArrowLeft size={16} /> Tutte le news
            </Link>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 font-condensed uppercase tracking-widest text-[12px] md:text-[13px]">
              <span className={`inline-block w-2 h-2 rounded-full ${dot}`} aria-hidden="true" />
              <span className="text-lime">● {article.rubric}</span>
              <span className="opacity-50">·</span>
              <span>{article.pillarPrimary}</span>
              {article.pillarsSecondary?.map((p) => (
                <React.Fragment key={p}>
                  <span className="opacity-50">·</span>
                  <span className="opacity-80">{p}</span>
                </React.Fragment>
              ))}
            </div>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <h1
              className="font-display mt-6 max-w-[22ch]"
              style={{
                lineHeight: 1.05,
                letterSpacing: "-0.015em",
                fontSize: "clamp(32px, 5.5vw, 56px)",
              }}
              data-testid="article-title"
            >
              {article.title}
            </h1>
          </RevealOnScroll>
          <RevealOnScroll delay={0.2}>
            <p
              className="mt-7 text-[18px] md:text-[20px] leading-[1.5] opacity-90 max-w-[760px]"
              style={{ fontFamily: "'Hanken Grotesk', sans-serif", fontStyle: "italic" }}
            >
              {article.subtitle}
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.28}>
            <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 font-condensed uppercase tracking-widest text-[12px] md:text-[13px] opacity-85">
              <span>{article.author}</span>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />
              <span>{article.readingTime} min lettura</span>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />
              <span>{formatNewsDate(article.publishedAt)}</span>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* CORPO */}
      <section className="bg-galactico text-dodgers py-16 md:py-20" data-testid="article-body">
        <div className="container-os">
          <div className="max-w-[760px] mx-auto">
            {article.body.map((b, i) => (
              <Block key={i} block={b} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA finale */}
      <section
        className="bg-dodgers text-galactico py-16 md:py-24 border-t border-galactico/10"
        data-testid="article-cta"
      >
        <div className="container-os">
          <div className="max-w-[760px] mx-auto">
            <h3 className="font-display leading-[1.1]" style={{ fontSize: "clamp(28px, 4vw, 36px)" }}>
              {article.cta.copy}
            </h3>
            <div className="mt-8">
              <Link to={article.cta.ctaHref} data-testid="article-cta-link">
                <Button variant="primary">{article.cta.ctaLabel}</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Fonti */}
      <section className="bg-galactico text-dodgers py-14 md:py-16" data-testid="article-references">
        <div className="container-os">
          <div className="max-w-[760px] mx-auto">
            <SectionEyebrow>Fonti e riferimenti</SectionEyebrow>
            <ol
              className="mt-6 space-y-3 text-[14px] leading-[1.6] text-dodgers/85"
              style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
            >
              {article.references.map((r) => (
                <li key={r.num} className="flex gap-3">
                  <span className="text-lime font-bold flex-shrink-0">[{r.num}]</span>
                  <span>{r.text}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Articoli correlati */}
      {related.length > 0 && (
        <section
          className="bg-galactico text-dodgers pb-20 md:pb-28"
          data-testid="article-related"
        >
          <div className="container-os">
            <div className="border-t border-mandrogni/30 pt-14 md:pt-16">
              <SectionEyebrow>Continua a leggere</SectionEyebrow>
              <h2 className="display-h2 mt-4 mb-10">Articoli correlati.</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {related.map((a) => (
                  <NewsArticleCard key={a.slug} article={a} />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default NewsArticleLayout;
